import { FC, useDeferredValue, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Search } from '@mui/icons-material'
import { Box, InputAdornment, MenuItem, Skeleton, Stack, TextField, Typography } from '@mui/material'

import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetInternalImplementationGamesQuery } from '../../api/implementation-examples'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { ImplementationGamesTable } from '../../features/implementation-examples/components/ImplementationGamesTable/ImplementationGamesTable'
import { filterImplementationGames, toImplementationGameRows } from '../../features/implementation-examples/helpers/implementationExampleHelpers'
import { defaultImplementationMarketIso } from '../../features/implementation-examples/helpers/implementationExamplePaths'

export const ImplementationExamplesPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-implementation-examples'))
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const marketIso = searchParams.get('marketIso') || defaultImplementationMarketIso
  const term = searchParams.get('term') ?? ''
  const deferredTerm = useDeferredValue(term)
  const marketsQuery = useGetInternalMarketsQuery()
  const gamesQuery = useGetInternalImplementationGamesQuery(marketIso, { refetchOnMountOrArgChange: true })

  const setParam = (key: string, value: string, defaultValue: string) =>
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        if (value && value !== defaultValue) next.set(key, value)
        else next.delete(key)
        return next
      },
      { replace: true }
    )

  const markets = useMemo(() => [...(marketsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [marketsQuery.data])
  const allRows = useMemo(
    () => (gamesQuery.currentData ? toImplementationGameRows(gamesQuery.currentData.counts, gamesQuery.currentData.games) : []),
    [gamesQuery.currentData]
  )
  const rows = useMemo(() => filterImplementationGames(allRows, deferredTerm), [allRows, deferredTerm])

  const renderResults = () => {
    if (gamesQuery.isError) return <Typography color="error.main">{t('internal-analysts:implementation_games_load_failed')}</Typography>
    if (!gamesQuery.currentData) return <Skeleton variant="rectangular" height={480} />
    return <ImplementationGamesTable rows={rows} marketIso={marketIso} isLoading={gamesQuery.isFetching} />
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} spacing={2}>
        <TextField
          select
          size="small"
          label={t('internal-common:market')}
          value={marketIso}
          onChange={(event) => setParam('marketIso', event.target.value, defaultImplementationMarketIso)}
          error={marketsQuery.isError}
          helperText={marketsQuery.isError ? t('internal-analysts:games_markets_load_failed') : undefined}
          sx={{ minWidth: 220 }}
        >
          {markets.map((market) => (
            <MenuItem key={market.id} value={market.iso}>
              {market.name}
            </MenuItem>
          ))}
          {!markets.some(({ iso }) => iso === marketIso) && <MenuItem value={marketIso}>{marketIso}</MenuItem>}
        </TextField>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('internal-analysts:implementation_games_scope')}
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <TextField
          size="small"
          value={term}
          onChange={(event) => setParam('term', event.target.value, '')}
          placeholder={t('internal-analysts:implementation_games_search')}
          inputProps={{ 'aria-label': t('internal-analysts:implementation_games_search') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        {gamesQuery.currentData && (
          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
            {t('internal-analysts:implementation_games_count', { count: rows.length, total: allRows.length })}
          </Typography>
        )}
      </Stack>

      {renderResults()}
    </Box>
  )
}
